<script>
import FormsDateTimePicker from "@/components/Forms/DateTimePicker.vue";
import FormsValidatedInput from "@/components/Forms/ValidatedInput.vue";
import FileDownloadLink from "@/components/shared/FileDownloadLink.vue";
import YearlyIncomeReportsBox from "@/views/admin/YearlyIncomeReportsBox.vue";
import dayjs from "dayjs";

export default {
  name: "AdminReports",
  components: {
    FileDownloadLink,
    YearlyIncomeReportsBox,
    FormsValidatedInput,
    FormsDateTimePicker,
  },
  data() {
    return {
      balanceUpToDate: null,
      insurancePeriod: null,
      balanceNonZero: false,
    };
  },
  computed: {
    insurancePeriodOptions() {
      let options = [];
      let now = dayjs();
      let optionIndex = 0;

      let periodStart = dayjs("2020-03-01T00:00:00");

      while (periodStart.isSameOrBefore(now)) {
        let nextPeriodStart = periodStart.add(3, "months");

        let text = `${periodStart.format("YYYY-MM")} - ${nextPeriodStart.format("YYYY-MM")}`;
        if (periodStart.isSameOrBefore(now) && nextPeriodStart.isAfter(now)) {
          text += " (en cours)";
        }

        options.push({
          value: optionIndex++,
          text,
        });

        periodStart = nextPeriodStart;
      }
      return options.reverse();
    },
    insurancePeriodStart() {
      if (this.insurancePeriod === null) {
        return null;
      }
      return dayjs("2020-03-01T00:00:00")
        .add(this.insurancePeriod * 3, "months")
        .toISOString();
    },
    insurancePeriodEnd() {
      if (this.insurancePeriod === null) {
        return null;
      }
      return dayjs("2020-06-01T00:00:00")
        .add(this.insurancePeriod * 3, "months")
        .toISOString();
    },
  },
  methods: {
    formatDate(date) {
      return dayjs(date).format("D MMMM YYYY HH:mm z (Z)");
    },
    formatInsuranceReportName() {
      // Using period option text as shown in the period select box.
      return this.$t("views.admin.admin_reports.insurance.report_file_name", {
        period:
          this.insurancePeriodOptions[this.insurancePeriodOptions.length - this.insurancePeriod - 1]
            .text,
      });
    },
    formatInsuranceReportUrl() {
      let params = new URLSearchParams();
      if (this.insurancePeriodStart) {
        params.set("from", this.insurancePeriodStart);
      }
      if (this.insurancePeriodEnd) {
        params.set("to", this.insurancePeriodEnd);
      }
      return "/reports/insurance/generate?" + params.toString();
    },
    formatBalanceReportName() {
      return this.$t("views.admin.admin_reports.balance.report_file_name", {
        time: this.balanceUpToDate ?? dayjs().startOf("minute").toISOString(),
      });
    },
    formatBalanceReportUrl() {
      let params = new URLSearchParams();
      if (this.balanceUpToDate) {
        params.set("at", this.balanceUpToDate);
      }
      if (this.balanceNonZero) {
        params.set("non_zero_only", 1);
      }
      return "/reports/balance/generate?" + params.toString();
    },
  },
};
</script>

<template>
  <div>
    <h2>{{ $t("views.admin.admin_reports.title") }}</h2>
    <b-card>
      <h3>{{ $t("views.admin.admin_reports.insurance.title") }}</h3>
      <b-form>
        <b-form-group
          :label="$t('views.admin.admin_reports.insurance.period')"
          :description="
            insurancePeriod
              ? `${formatDate(insurancePeriodStart)} au ${formatDate(insurancePeriodEnd)}`
              : null
          "
        >
          <b-select v-model="insurancePeriod" :options="insurancePeriodOptions" />
        </b-form-group>

        <file-download-link
          v-if="insurancePeriod !== null"
          :api-url="formatInsuranceReportUrl()"
          download
          :filename="formatInsuranceReportName()"
          :button-label="$t('views.admin.admin_reports.insurance.generate_button')"
        />
      </b-form>
    </b-card>

    <b-card>
      <h3>{{ $t("views.admin.admin_reports.balance.title") }}</h3>
      <b-form>
        <b-form-group
          :label="$t('views.admin.admin_reports.balance.date_label')"
          :description="$t('views.admin.admin_reports.balance.date_description')"
        >
          <forms-date-time-picker v-model="balanceUpToDate" clearable show-timezone />
        </b-form-group>
        <forms-validated-input
          v-model="balanceNonZero"
          type="checkbox"
          name="non-zero"
          :label="$t('views.admin.admin_reports.balance.non_zero_only')"
        />
        <file-download-link
          :api-url="formatBalanceReportUrl()"
          download
          :filename="formatBalanceReportName()"
          :button-label="$t('views.admin.admin_reports.balance.generate_button')"
        />
      </b-form>
    </b-card>
    <yearly-income-reports-box />
  </div>
</template>

<style scoped lang="scss">
.card {
  box-shadow: $small-shadow;
  + .card {
    margin-top: 1rem;
  }
}
</style>
